import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import { db } from '../config/firebase';
import { getCloudFunctionUrl } from '../config/firebaseFunctionUrl';
import { doc, getDoc } from 'firebase/firestore';
import { generateVerticalPDF, generateCoverPDF } from '../services/pdfService';
import { uploadPDF } from '../services/firebase/storage';
import { getBook, updateBookPdfUrls } from '../services/bookService';

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('initial');
  const [printJobId, setPrintJobId] = useState(null);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);

  const callFinalize = useCallback(async (payload) => {
    const auth = getAuth();
    let user = auth.currentUser;
    
    // Wait for user to be authenticated if not ready
    if (!user) {
      await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
          if (currentUser) {
            user = currentUser;
            unsubscribe();
            resolve();
          }
        });
      });
    }
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const idToken = await user.getIdToken();
    const resp = await fetch(getCloudFunctionUrl('finalizePaidOrder'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(payload)
    });
    const json = await resp.json().catch(() => ({}));
    return { resp, json };
  }, []);

  const ensurePdfs = useCallback(async (sessionId) => {
    // 1) Look up intent to find bookId and orderData
    const intentSnap = await getDoc(doc(db, 'checkoutIntents', sessionId));
    if (!intentSnap.exists()) throw new Error('Checkout intent not found');
    const intent = intentSnap.data();
    const bookId = intent.bookId || intent.orderData?.bookId;
    if (!bookId) throw new Error('Book reference missing in checkout intent');

    // 2) Check existing book pdfUrls
    const latestBook = await getBook(bookId);
    let coverUrl = latestBook?.pdfUrls?.cover || null;
    let interiorUrl = latestBook?.pdfUrls?.verticalContent || latestBook?.pdfUrls?.interior || null;

    // 3) If missing, generate and upload now
    if (!coverUrl || !interiorUrl) {
      const toastId = toast.loading('Preparing book files after payment...');
      try {
        const bindingType = (typeof intent.orderData?.printSpecId === 'string' && intent.orderData.printSpecId.includes('PB')) ? 'perfect-bound' : 'perfect-bound';
        const coverPdf = await generateCoverPDF(latestBook, { forLuluPrinting: true, bindingType });
        const interiorPdf = await generateVerticalPDF(latestBook, { forLuluPrinting: true, skipCover: true, bindingType });

        const [coverUp, interiorUp] = await Promise.all([
          uploadPDF(coverPdf, bookId, 'cover'),
          uploadPDF(interiorPdf, bookId, 'verticalContent')
        ]);

        if (!coverUp?.url || !interiorUp?.url) throw new Error('Failed to upload generated PDFs');

        await updateBookPdfUrls(bookId, { cover: coverUp.url, verticalContent: interiorUp.url });
        coverUrl = coverUp.url;
        interiorUrl = interiorUp.url;
        toast.update(toastId, { render: 'Book files prepared', type: 'success', isLoading: false, autoClose: 1500 });
      } catch (e) {
        toast.update(toastId, { render: e.message || 'Failed to prepare book files', type: 'error', isLoading: false, autoClose: 3500 });
        throw e;
      }
    }

    return { coverUrl, interiorUrl };
  }, []);

  const finalize = async (sessionId) => {
    try {
      setStatus('finalizing');
      setError(null);
      // First try sessionId-only (works with updated function)
      let { resp, json } = await callFinalize({ sessionId });
      if (!resp.ok || !json?.ok) {
        const msg = json?.error || '';
        const needsUrls = /coverPdfUrl|interiorPdfUrl/i.test(msg);
        if (needsUrls || resp.status === 400) {
          // Fetch or generate PDFs, then retry including URLs (compatible with older function)
          const { coverUrl, interiorUrl } = await ensurePdfs(sessionId);
          ({ resp, json } = await callFinalize({ sessionId, coverPdfUrl: coverUrl, interiorPdfUrl: interiorUrl }));
          if (!resp.ok || !json?.ok) {
            throw new Error(json?.error || 'Failed to finalize order');
          }
        } else if (resp.status === 409) {
          // 409 from updated function when PDFs missing: self-heal
          const { coverUrl, interiorUrl } = await ensurePdfs(sessionId);
          ({ resp, json } = await callFinalize({ sessionId, coverPdfUrl: coverUrl, interiorPdfUrl: interiorUrl }));
          if (!resp.ok || !json?.ok) {
            throw new Error(json?.error || 'Failed to finalize order');
          }
        } else {
          throw new Error(json?.error || 'Failed to finalize order');
        }
      }
      setPrintJobId(json.printJobId || null);
      setStatus('done');
      toast.success('Payment confirmed. Print job created.');
      if (json.printJobId) {
        setTimeout(() => navigate(`/account/print-jobs/${json.printJobId}`), 800);
      }
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Finalize failed');
      toast.error(e.message || 'Finalize failed');
    }
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setError('Missing checkout session');
      setStatus('error');
      return;
    }
    
    hasInitialized.current = true;
    finalize(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg p-6 text-center">
        {status === 'finalizing' && (
          <>
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
            <h2 className="text-lg font-semibold">Finalizing your order…</h2>
            <p className="text-sm text-gray-600 mt-2">Creating your print job and preparing details.</p>
          </>
        )}
        {status === 'done' && (
          <>
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="h-7 w-7 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L8.5 12.086l6.793-6.793a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
            </div>
            <h2 className="text-lg font-semibold">Payment successful</h2>
            <p className="text-sm text-gray-600 mt-2">Your print job is ready.</p>
            <div className="mt-4 space-x-2">
              {printJobId ? (
                <button onClick={() => navigate(`/account/print-jobs/${printJobId}`)} className="px-4 py-2 bg-indigo-600 text-white rounded-md">View Print Job</button>
              ) : null}
              <button onClick={() => navigate('/library')} className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md">Back to Library</button>
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="h-7 w-7 text-red-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/></svg>
            </div>
            <h2 className="text-lg font-semibold">We couldn’t finalize the order</h2>
            <p className="text-sm text-gray-600 mt-2">{error}</p>
            <div className="mt-4 space-x-2">
              <button onClick={() => {
                const params = new URLSearchParams(location.search);
                const sessionId = params.get('session_id');
                if (sessionId) finalize(sessionId);
              }} className="px-4 py-2 bg-indigo-600 text-white rounded-md">Try Again</button>
              <button onClick={() => navigate('/ship')} className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md">Return to Ship</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
